/**
 * MotionWaveSelectPanel — AG-UI wave curation panel.
 *
 * UX (per IVTHubSpec tech/motion/agui-wave-select.md):
 *   1. Panel opens WITH a list: the Motion's assets, then the opportunities the
 *      offline judge scored against them (ListRelevantPipelines, DAT-8634).
 *   2. User narrows it with the controls, or in words via "Refine with Rocky" →
 *      `ivthub-relevance-query` returns a `query_spec` and the panel re-queries.
 *   3. "Upload Opportunity List" still goes to `ivthub-motion-wave-select`,
 *      which returns a candidate SET on `results` rather than a query.
 *   4. Whichever arrives, the panel renders one table. `results` wins when both
 *      are present: an explicit set is a narrower statement than a query.
 *   5. User checks rows, clicks "Confirm & Add to Motion" → ClaimAccounts
 *      writes to the working pools.
 *
 * Two snapshot shapes, two meanings — see the `usingApiRows` comment below.
 *
 * Architectural notes
 * - **Side-channel only, never reads chat stream**. State arrives via
 *   useAgentState('ivt.motion.wave'), which is updated by ChatPanel's
 *   state_snapshot SSE case (see stores/agentStateStore.ts).
 * - **Selection is local component state**, not pushed back into the
 *   shared store. The store represents what Rocky has prepared; user
 *   selection is the human committing to a subset.
 * - **Cloudscape Table** handles selection, sorting, and expandable rows
 *   natively (cf. ScheduledTasksTab.tsx for the canonical pattern).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Container,
  Flashbar,
  FormField,
  Header,
  Link,
  SegmentedControl,
  SpaceBetween,
  Spinner,
  StatusIndicator,
  Table,
  TokenGroup,
  type FlashbarProps,
  type TableProps,
} from '@amzn/awsui-components-react';

// Real client is wired (IVTHubTSClient regenerated to include ClaimCandidates
// + PoolType.EXPLORE, backend C5 shipped in CR-285988352). buildClaims + the
// Confirm handler use `new ClaimCandidatesCommand({...})` — wave_id is a
// client-generated opaque id; the backend UPSERTs a motion_waves row from it,
// there is no separate CreateWave call.
import {
  ClaimCandidatesCommand,
  GetMotionCommand,
  ListRelevantPipelinesCommand,
  getIvtClient,
  type RelevanceScoreCounts,
  type RelevantPipelineItem,
} from '../../client/IvtHubClient';
import { newWaveId } from '../../utils/waveId';
import { toCandidateAccounts } from './relevanceAdapter';
import {
  describeSpec,
  removeToken,
  resolveAssetIds,
  resolveMinRelevance,
  specKey,
  toListRequest,
} from './relevanceQuery';
import type { RelevanceQuerySpec } from './relevanceQuery';
import { useChatPanel } from '../../../context/ChatPanelContext';
import { useAgentState } from '../../../stores/agentStateStore';
import { oppSfdcUrl } from '../../utils/sfdc';
// Reuse the same tag chip renderer the Motion detail page's Pipeline table
// uses — same visual language (grey rounded chips + "+N" overflow), so tags
// look consistent across all Motion-adjacent tables.
import OverflowChips from '../../pages/MotionsPage/components/OverflowChips';
import type {
  AccountTableRow,
  CandidateAccount,
  CandidateOpp,
  Claim,
  ClaimAccountsResult,
  OppTableRow,
  TableRow,
  WaveSnapshot,
  WaveRow,
} from './types';
import { isAccountRow, isOppRow } from './types';
import { denialHint } from '../../utils/authorization';

export interface MotionWaveSelectPanelProps {
  /**
   * The Motion to add the selected candidates into. Opaque server-generated
   * TEXT id (32-char lowercase hex per Smithy MotionId pattern) — do NOT
   * treat as a number, parseInt() will truncate at the first non-digit.
   */
  motionId: string;
  /** Called after a successful ClaimCandidates write (e.g. to close the drawer / nav back). */
  onConfirmed?: (claimed: number, skipped: number) => void;
  /** Called when the user cancels. */
  onCancel?: () => void;
}

const NAMESPACE = 'ivt.motion.wave';

// One page of the default list. The operation caps at 500; this is a curation
// screen, so the ceiling that matters is how many rows a human will scan before
// refining with Rocky rather than what the backend can return.
const DEFAULT_PAGE_SIZE = 100;

// Relevance floor the default list asks for, sent explicitly so the value is
// visible here rather than inherited invisibly from the backend (which would
// default to 4). 3 is "different scenario but the capability could still cover
// it" — wider than 4 without dragging in the 1s and 2s, which are the judge
// saying it found no basis and vastly outnumber the rest.
//
// Overridable: a query_spec on the AGUI snapshot wins, see effectiveMinRelevance.
const DEFAULT_MIN_RELEVANCE = 3;

// Columns with no source in ListRelevantPipelines. Hidden while the default
// list is showing rather than rendered blank — see relevanceAdapter for why
// each is absent. `score` stays but is relabelled: it means 1-5 relevance from
// this source and the skill's own account score from the other.
const COLUMNS_WITHOUT_RELEVANCE_SOURCE = ['industry', 'tags', 'action'] as const;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function fmtArr(val: number | undefined | null): string {
  if (val == null) return '-';
  if (val >= 1_000_000) return `$${(val / 1_000_000).toFixed(1)}M`;
  if (val >= 1000) return `$${Math.round(val / 1000)}K`;
  return `$${Math.round(val)}`;
}

function sumOppArr(opps: CandidateOpp[]): number {
  return opps.reduce((s, o) => s + (o.arr ?? 0), 0);
}

// ---------------------------------------------------------------------------
// Stage → Cloudscape StatusIndicator type. Unknown stages fall back to
// neutral grey. Mapping is intentionally minimal — sales pipeline stages
// drift over time; we colour the common cases and let the rest render plain.
// ---------------------------------------------------------------------------
type StatusType = React.ComponentProps<typeof StatusIndicator>['type'];

function stageStatus(stage: string): StatusType {
  const s = stage.toLowerCase();
  if (s.includes('committed') || s.includes('closed won')) return 'success';
  // `lost` check must precede the `opportunity` check below — a stage like
  // "Lost Opportunity" matches BOTH, and without this ordering it would be
  // misclassified as `pending` instead of `error`.
  if (s.includes('lost')) return 'error';
  if (s.includes('business validation') || s.includes('technical validation')) return 'in-progress';
  if (s.includes('qualified') || s.includes('prospect') || s.includes('opportunity')) return 'pending';
  return 'stopped';
}

// ---------------------------------------------------------------------------
// Row identity key — Cloudscape Table needs a stable, unique trackBy across
// the combined account + opp rows. Opp ids are unique on their own, but
// account_id and opp_id share a 15/18-char SFDC namespace, so we prefix the
// row kind to keep parents and children from colliding.
// ---------------------------------------------------------------------------
function trackBy(r: TableRow): string {
  return isAccountRow(r)
    ? `a:${r.account_id}`
    : `o:${r.parent_account_id}:${r.opp_id}`;
}

// ---------------------------------------------------------------------------
// Column definitions — rows are AccountTableRow | OppTableRow. Each cell
// branches on row kind so a single column serves both shapes; Cloudscape's
// expandableRows API requires parents and children to share columns.
// ---------------------------------------------------------------------------

const COLUMN_IDS = [
  'account',
  'industry',
  'stage',
  'tags',
  'score',
  'reasons',
  'action',
  'opps',
  'owner',
] as const;

const COLUMN_MIN_WIDTH: Record<(typeof COLUMN_IDS)[number], number> = {
  account: 200,
  industry: 140,
  stage: 140,
  tags: 160,
  score: 90,
  reasons: 180,
  action: 180,
  opps: 140,
  owner: 110,
};

const WIDTHS_STORAGE_KEY = 'ivt_motion_wave_select_col_widths';

function loadColumnWidths(): Record<string, number> {
  try {
    const stored = localStorage.getItem(WIDTHS_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as Record<string, number>;
      return Object.fromEntries(
        Object.entries(parsed).filter(
          ([id, w]) => COLUMN_IDS.includes(id as (typeof COLUMN_IDS)[number]) && typeof w === 'number' && w > 0,
        ),
      );
    }
  } catch {
    /* ignore */
  }
  return {};
}

function saveColumnWidths(widths: Record<string, number>) {
  try {
    localStorage.setItem(WIDTHS_STORAGE_KEY, JSON.stringify(widths));
  } catch {
    /* ignore */
  }
}

const COLUMNS: TableProps.ColumnDefinition<TableRow>[] = [
  {
    id: 'account',
    header: 'Account / Opp',
    cell: (r) =>
      isAccountRow(r) ? (
        <SpaceBetween direction="horizontal" size="xs">
          <span>{r.account_name}</span>
          {r.flags.map((f, i) => (
            <span key={i} title={f.tip}>{f.icon}</span>
          ))}
        </SpaceBetween>
      ) : (
        <Box variant="small" color="text-body-secondary">
          <Link href={oppSfdcUrl(r.opp_id)} external target="_blank">
            {r.opp_name}
          </Link>
        </Box>
      ),
    sortingField: 'account_name',
    isRowHeader: true,
  },
  {
    id: 'industry',
    header: 'Industry',
    cell: (r) => (isAccountRow(r) ? r.industry : ''),
    sortingField: 'industry',
  },
  {
    id: 'stage',
    header: 'Stage',
    cell: (r) =>
      isAccountRow(r) ? (
        ''
      ) : (
        <StatusIndicator type={stageStatus(r.stage)}>{r.stage}</StatusIndicator>
      ),
  },
  {
    // IVT tags attached to the opp (GCR-IVT-* namespace, filtered server-side
    // by the skill via share.v_opportunity_tag_territory). Account rows leave
    // this blank — tags are only meaningful at opp granularity in the current
    // data model. Renderer matches Motion detail page's Pipeline table (same
    // OverflowChips component + "+N" fold behaviour).
    id: 'tags',
    header: 'Tags',
    cell: (r) => {
      if (isAccountRow(r)) return '';
      const tags = r.tags ?? [];
      return (
        <OverflowChips
          ariaLabel="Tags"
          items={tags.map((t) => ({ key: t, label: t }))}
        />
      );
    },
  },
  {
    id: 'score',
    header: 'Score',
    cell: (r) => (isAccountRow(r) ? r.score : ''),
    sortingField: 'score',
  },
  {
    id: 'reasons',
    header: 'Match reasons',
    cell: (r) => {
      // Both account and opp rows carry `reasons` (opp-level added in L1
      // via skill `_derive_opp_reasons_action`). Render identically so the
      // column is consistent whether the row is a parent or a child.
      const reasons = isAccountRow(r) ? r.reasons : (r.reasons ?? []);
      if (reasons.length === 0) return '';
      return (
        <SpaceBetween direction="horizontal" size="xxs">
          {reasons.map((rr, i) => (
            <Box key={i} variant="small" color="text-status-info">
              {rr.text}
            </Box>
          ))}
        </SpaceBetween>
      );
    },
  },
  {
    id: 'action',
    header: 'Suggested action',
    cell: (r) => {
      // Account.action is required in the type; Opp.action is optional (L1
      // enrichment; older cached snapshots may not carry it). Mirror the
      // `reasons` column's pattern so the intent is explicit at each branch.
      const action = isAccountRow(r) ? r.action : (r.action ?? '');
      return action ? <Box variant="small">{action}</Box> : '';
    },
  },
  {
    id: 'opps',
    header: 'Open opps / ARR',
    cell: (r) =>
      isAccountRow(r)
        ? r.open_opps.length === 0
          ? '-'
          : `${r.open_opps.length} opp · ${fmtArr(sumOppArr(r.open_opps))}`
        : (
          <Box variant="small" color="text-body-secondary">
            {fmtArr(r.arr)}
          </Box>
        ),
  },
  {
    id: 'owner',
    header: 'Owner',
    cell: (r) => (
      <Box variant="small" color="text-body-secondary">
        {r.owner ?? ''}
      </Box>
    ),
  },
];

// ---------------------------------------------------------------------------
// Row materialisation — flatten the snapshot into an `AccountTableRow[]`
// (top-level rows). Opps live as children, surfaced through
// `expandableRows.getItemChildren`. This keeps Cloudscape's tree-of-rows
// API happy while preserving each account's open_opps list for non-render
// uses (sort comparators, summary counters).
// ---------------------------------------------------------------------------
function toAccountRows(rows: WaveRow[]): AccountTableRow[] {
  return rows.map((a) => ({ ...a, kind: 'account' }));
}

function toOppChildren(a: AccountTableRow): OppTableRow[] {
  // Dedupe by opp_id — the skill already GROUP BYs at the opp grain, but
  // any future data-source drift (e.g. adding a new fact table that
  // splits rows again) would collapse into a single visible row on
  // expand/collapse because Cloudscape's trackBy sees identical keys.
  // Keep the first occurrence (already ARR-DESC sorted by the skill).
  const seen = new Set<string>();
  const out: OppTableRow[] = [];
  for (const o of a.open_opps) {
    if (seen.has(o.opp_id)) continue;
    seen.add(o.opp_id);
    out.push({
      ...o,
      kind: 'opp',
      parent_account_id: a.account_id,
      parent_account_name: a.account_name,
    });
  }
  return out;
}

// ---------------------------------------------------------------------------
// Claim flatten — Cloudscape selection is two-level (account row + opp
// children); IVTHub `Claim` is flat: each entry is either an account-level
// claim (poolType=EXPLORE, account-only fields) or an opp-level claim
// (poolType=ACCELERATE, all fields populated). We emit both buckets
// independently so a user who ticks an account AND some of its opps in the
// same wave lands one row in motion_accounts + N rows in motion_opps.
// ---------------------------------------------------------------------------
function buildClaims(
  selection: { accountIds: string[]; oppsByAccount: Map<string, string[]> },
  accountById: Map<string, AccountTableRow>,
): Claim[] {
  const claims: Claim[] = [];
  // EXPLORE bucket: one entry per ticked account row.
  for (const accountId of selection.accountIds) {
    const acc = accountById.get(accountId);
    if (!acc) continue;
    claims.push({
      accountId,
      accountName: acc.account_name,
      poolType: 'EXPLORE',
      matchReason: flattenReasons(acc.reasons),
      suggestedAction: nonEmpty(acc.action),
    });
  }
  // ACCELERATE bucket: one entry per ticked opp child, regardless of
  // whether its parent account was also ticked. The backend writes them
  // to motion_opps; motion_accounts gets the parent independently via
  // the EXPLORE claim above (if any).
  for (const [accountId, oppIds] of selection.oppsByAccount.entries()) {
    const acc = accountById.get(accountId);
    if (!acc) continue;
    for (const oppId of oppIds) {
      const opp = acc.open_opps.find((o) => o.opp_id === oppId);
      if (!opp) continue;
      // Prefer opp-level rationale (L1 skill `_derive_opp_reasons_action`);
      // fall back to the parent account's when the opp didn't produce one.
      const reasons = opp.reasons?.length ? opp.reasons : acc.reasons;
      const action = opp.action ?? acc.action;
      claims.push({
        accountId,
        accountName: acc.account_name,
        opptyId: opp.opp_id,
        oppName: opp.opp_name,
        arr: opp.arr,
        oppStage: opp.stage,
        poolType: 'ACCELERATE',
        matchReason: flattenReasons(reasons),
        suggestedAction: nonEmpty(action),
      });
    }
  }
  return claims;
}

// Flatten CandidateReason[] into a single ≤4096-char line. Backend Smithy
// caps at 4096; skill outputs a handful of tokens per reason so overflow is
// unlikely in practice, but we still trim to be safe and stay decoded on
// the wire (drop trailing partial reason rather than mid-string cut).
function flattenReasons(reasons: readonly { text: string }[] | undefined): string | undefined {
  if (!reasons || reasons.length === 0) return undefined;
  const parts = reasons.map((r) => r.text).filter((t) => t && t.trim().length > 0);
  if (parts.length === 0) return undefined;
  const joined = parts.join(' · ');
  return joined.length <= 4096 ? joined : joined.slice(0, 4096);
}

function nonEmpty(s: string | undefined): string | undefined {
  const t = s?.trim();
  return t && t.length > 0 ? (t.length <= 4096 ? t : t.slice(0, 4096)) : undefined;
}

// ---------------------------------------------------------------------------
// wave_id generator — wave is a front-end batching concept post-C5, so we
// mint an opaque id here and let the backend UPSERT the motion_waves row on
// receipt. 32-char lowercase hex (random UUIDv4 without hyphens) matches the
// server's WaveId Smithy pattern `^[a-z0-9]+$`.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function MotionWaveSelectPanel({
  motionId,
  onConfirmed,
  onCancel,
}: MotionWaveSelectPanelProps) {
  const snapshot = useAgentState<WaveSnapshot>(NAMESPACE);
  const { sendPrompt, chatBusy, setWaveQueryScope } = useChatPanel();

  // ---- Default candidate list, straight from the API ----------------------
  // The panel used to open EMPTY, so the first thing a user saw was a prompt to
  // go ask Rocky. It now loads a default recommendation on mount: the Motion's
  // assets, then the opportunities the offline judge scored against them.
  //
  // Server defaults do the work — relevance >= 4, already-tagged pairs
  // excluded, terminal stages excluded — so this list is "what should we claim",
  // not "everything that exists".
  //
  // State holds the raw ITEMS, not the grouped rows: the sort is by relevance
  // and not by account, so one account's opportunities routinely span pages.
  // Re-grouping the accumulated list on each page is what merges them; grouping
  // per page and concatenating would emit duplicate account rows.
  //
  // The query being run. Seeded from Rocky's spec and then OWNED BY THE USER —
  // every condition Rocky sets is rendered as a dismissible token, and clearing
  // one edits this state. Held here rather than read straight off the snapshot so
  // those edits survive; a snapshot only replaces it when Rocky sends a
  // genuinely different spec (see the sync effect below).
  const [spec, setSpec] = useState<RelevanceQuerySpec>(() => snapshot?.query_spec ?? {});
  const incomingSpecKey = specKey(snapshot?.query_spec);

  useEffect(() => {
    // Keyed on the spec's VALUE, not the snapshot object: the store is written on
    // every agent turn, and comparing by identity would throw away the user's
    // edits each time an unrelated field changed.
    setSpec(snapshot?.query_spec ?? {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incomingSpecKey]);

  // The floor the query will actually run with, from the same function the request
  // uses. Reading spec.minRelevanceScore raw let the control and the empty state
  // report a number the request had already clamped away.
  const effectiveMinRelevance = resolveMinRelevance(spec, DEFAULT_MIN_RELEVANCE);

  const activeSpecKey = specKey(spec);

  // Publish the live spec into the chat's context.items so EVERY message the user
  // sends carries it, not just the ones sent by the button.
  //
  // The button's prompt already names the current filters, but that only helps
  // when the button is clicked. A user who dismisses a chip and then types in chat
  // has removed a condition from the panel and not from the conversation — so the
  // agent rebuilds its previous spec and puts it straight back, with nothing on
  // screen to explain why the filter returned.
  //
  // Cleared on unmount: the spec belongs to this Motion's curation session, and
  // leaving it behind would attach a stale filter to conversations on other pages.
  useEffect(() => {
    setWaveQueryScope({ motionId, specJson: JSON.stringify(spec) });
    return () => setWaveQueryScope(null);
    // Keyed by value like the effect above, for the same reason: `spec` is a fresh
    // object every render, so depending on it would republish the scope on every
    // keystroke elsewhere in the panel. exhaustive-deps cannot see that
    // activeSpecKey is derived from it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [motionId, activeSpecKey, setWaveQueryScope]);

  const [apiItems, setApiItems] = useState<RelevantPipelineItem[] | null>(null);
  const [apiCounts, setApiCounts] = useState<RelevanceScoreCounts | null>(null);
  const [apiToken, setApiToken] = useState<string | undefined>(undefined);
  const [apiAssetIds, setApiAssetIds] = useState<string[]>([]);
  const [apiLoading, setApiLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  // Failure of a Load more, kept apart from apiError because the two surface in
  // different places: a first-page failure has an empty table to fill, a later
  // page has rows already on screen and a button to retry with.
  const [moreError, setMoreError] = useState<string | null>(null);

  // Declared after apiAssetIds because the asset token names the assets that
  // survive the intersection with the Motion's, which is only knowable once
  // GetMotion has answered. Before then the spec's own list stands.
  const specTokens = useMemo(() => describeSpec(spec, apiAssetIds), [spec, apiAssetIds]);

  // The spec asked for assets and this Motion has none of them, so no query ran.
  // Derived rather than stored: it is a fact about the two asset sets, and a flag
  // set in the fetch would have to be cleared on every path that changes either.
  const assetsExcluded =
    !!spec.assetIds?.length &&
    apiAssetIds.length > 0 &&
    resolveAssetIds(spec, apiAssetIds).length === 0;

  const hasSnapshotResults = !!snapshot?.results?.length;

  // Which result set the rows on screen belong to. Bumped whenever the fetch
  // effect starts over, so an in-flight `loadMore` can tell that the list it was
  // paging no longer exists. A boolean like the effect's `cancelled` is not
  // enough here: loadMore is not owned by an effect, so it has no cleanup to
  // flip, and it has to compare against whatever the CURRENT set is.
  const fetchGen = useRef(0);

  useEffect(() => {
    // Rocky already chose the candidates, so anything fetched here would never be
    // rendered — `rows` prefers `snapshot.results`. Skipping saves two requests on
    // every open where a snapshot is already in the store from an earlier chat.
    // Keyed on the flag rather than the snapshot object so that clearing the
    // snapshot brings the API list back, but a snapshot refresh that only changes
    // its contents does not re-trigger a fetch nobody is waiting for.
    if (hasSnapshotResults) {
      setApiLoading(false);
      return;
    }
    let cancelled = false;
    setApiLoading(true);
    setApiError(null);
    // Clear the previous page BEFORE the new fetch, not after it resolves. A
    // changed floor or Motion is a different result set, and until the reply
    // arrives the old token is the live one: the Load more footer would still be
    // showing, and clicking it would send the NEW minRelevanceScore with the OLD
    // cursor — a fingerprint mismatch, so a 400 the user can do nothing about.
    // Dropping the rows too, since for a Motion change they belong to a
    // different Motion entirely.
    setApiToken(undefined);
    setApiItems(null);
    setMoreError(null);
    // Synchronous, so any loadMore already awaiting sees a different value the
    // moment this runs rather than after the new page lands.
    fetchGen.current += 1;
    (async () => {
      try {
        const client = getIvtClient();
        // Assets first: relevance is judged per (opportunity x asset), and the
        // Motion-to-Asset link is the Motion API's business. ListRelevantPipelines
        // deliberately takes no motionId, so resolving it here keeps that join
        // in one place rather than duplicating it server-side.
        const motion = await client.send(new GetMotionCommand({ motionId }));
        const assetIds = motion.motion?.assetIds ?? [];
        if (cancelled) return;
        if (assetIds.length === 0) {
          // Not an error: a Motion with no linked Asset has nothing to be
          // relevant TO, and saying so beats an empty table.
          setApiItems([]);
          setApiCounts(null);
          return;
        }
        // Set before the query, not after, because the asset token is derived from
        // it — the user has to be able to see that an asset they asked for is not
        // part of this Motion, including when the query below never runs.
        setApiAssetIds(assetIds);
        if (resolveAssetIds(spec, assetIds).length === 0) {
          // The spec named assets, none of them this Motion's. Sending the empty
          // intersection would drop the asset restriction server-side and answer
          // with every opportunity the user can see, so the query is skipped and
          // the empty state explains why rather than showing other Motions' rows.
          setApiItems([]);
          setApiCounts(null);
          return;
        }
        const out = await client.send(
          new ListRelevantPipelinesCommand(
            toListRequest(spec, {
              motionAssetIds: assetIds,
              defaultMinRelevance: DEFAULT_MIN_RELEVANCE,
              maxResults: DEFAULT_PAGE_SIZE,
            }),
          ),
        );
        if (cancelled) return;
        setApiItems(out.items ?? []);
        setApiCounts(out.scoreCounts ?? null);
        setApiToken(out.nextToken);
      } catch (e) {
        if (!cancelled) {
          setApiItems([]);
          setApiError(e instanceof Error ? e.message : String(e));
        }
      } finally {
        if (!cancelled) setApiLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [motionId, activeSpecKey, hasSnapshotResults]);

  // Next page. Every other argument must be byte-identical to the first call:
  // the token carries a fingerprint of the filter set it was issued under, and a
  // mismatch is a 400 rather than a silent re-page. That is why assetIds is held
  // in state instead of being re-resolved from GetMotion here.
  const loadMore = useCallback(async () => {
    if (!apiToken || loadingMore) return;
    setLoadingMore(true);
    // Its own error state, not apiError: that one is rendered in the Table's
    // `empty` slot, which by definition only shows when there are NO rows —
    // and loadMore only runs when there are. Reusing it made a failed page
    // completely silent, the button just stopped spinning.
    setMoreError(null);
    // Captured before the await. The closure also holds the old token, assetIds
    // and floor, so if the effect restarts mid-flight this page belongs to a
    // result set that no longer exists — appending it would mix two filter
    // fingerprints in one table, and reinstating its token would make the next
    // Load more send an old cursor with the new floor.
    const gen = fetchGen.current;
    try {
      const out = await getIvtClient().send(
        // The SAME spec as page 1. The cursor carries a fingerprint of the filter
        // set it was issued under, so any difference here is a 400 rather than a
        // silently re-paged result.
        new ListRelevantPipelinesCommand(
          toListRequest(spec, {
            motionAssetIds: apiAssetIds,
            defaultMinRelevance: DEFAULT_MIN_RELEVANCE,
            maxResults: DEFAULT_PAGE_SIZE,
            nextToken: apiToken,
          }),
        ),
      );
      if (gen !== fetchGen.current) return;
      // Append the raw items; the grouping memo below merges any that belong to
      // an account already on screen.
      setApiItems((prev) => [...(prev ?? []), ...(out.items ?? [])]);
      setApiToken(out.nextToken);
    } catch (e) {
      // Nothing to report if the list this failed against is already gone —
      // the error would describe a query the user is no longer looking at.
      if (gen !== fetchGen.current) return;
      setMoreError(e instanceof Error ? e.message : String(e));
    } finally {
      // Cleared unconditionally: the button belongs to the current render, and
      // leaving it spinning would outlive the request either way.
      setLoadingMore(false);
    }
  }, [apiAssetIds, apiToken, loadingMore, spec]);

  const apiRows: CandidateAccount[] | null = useMemo(
    () => (apiItems === null ? null : toCandidateAccounts(apiItems)),
    [apiItems],
  );

  // Two things Rocky can send, and they mean different things.
  //
  // `results` is an explicit candidate set: Rocky has already decided WHICH
  // opportunities, so those rows are shown verbatim and the API list steps
  // aside. Merging the two would leave the user unable to tell which rows came
  // from their own refinement.
  //
  // `query_spec` is the opposite — Rocky decided WHAT TO ASK and the panel still
  // does the asking (the AGUI side-channel cannot carry a full candidate set).
  // So a spec-only snapshot keeps the API path, just with Rocky's parameters:
  // `effectiveMinRelevance` above already folded the floor in, and re-running the
  // fetch effect on it replaces the accumulated pages rather than appending to
  // them — a different floor is a different result set, not more of the same one.
  const usingApiRows = !snapshot?.results?.length;
  const rows: WaveRow[] = useMemo(
    () => (snapshot?.results?.length ? snapshot.results : (apiRows ?? [])),
    [snapshot, apiRows],
  );
  const [confirming, setConfirming] = useState(false);
  // Top-level rows are accounts (with `kind: 'account'`); opps surface as
  // children via expandableRows.getItemChildren so they render inline beneath
  // their account row instead of being detached at the bottom of the table.
  const accountRows: AccountTableRow[] = useMemo(() => toAccountRows(rows), [rows]);

  // Selection — Cloudscape gives one flat `selectedItems` array across the
  // expandable tree; we keep that shape verbatim so checkbox / "select all"
  // bookkeeping stays Cloudscape-native, then derive the claim payload at
  // confirm time. Account ✓ = the whole account (opps come along for free);
  // Opp ✓ on its own = just this opp goes into the wave; both ticked is a
  // benign transient state — the account selection wins at Confirm.
  const [selected, setSelected] = useState<TableRow[]>([]);
  const [expandedItems, setExpandedItems] = useState<AccountTableRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(loadColumnWidths);

  const columnDefinitions: TableProps.ColumnDefinition<TableRow>[] = useMemo(
    () =>
      COLUMNS.filter(
        (c) =>
          !usingApiRows ||
          !COLUMNS_WITHOUT_RELEVANCE_SOURCE.includes(
            c.id as (typeof COLUMNS_WITHOUT_RELEVANCE_SOURCE)[number],
          ),
      ).map((c) => {
        const id = c.id as (typeof COLUMN_IDS)[number];
        return {
          ...c,
          // Relevance is judged per OPPORTUNITY, so in this mode the score and
          // its reason render on the opp rows and the account row stays blank.
          // The default `score` cell does the opposite — account-only — because
          // the skill's score IS an account-level number. Sorting is dropped
          // with it: a column whose parent rows are all empty cannot order them.
          ...(usingApiRows && id === 'score'
            ? {
                header: 'Relevance',
                cell: (r: TableRow) => (isOppRow(r) ? (r.relevance_score ?? '') : ''),
                sortingField: undefined,
              }
            : {}),
          minWidth: COLUMN_MIN_WIDTH[id],
          ...(columnWidths[id] ? { width: columnWidths[id] } : {}),
        };
      }),
    [columnWidths, usingApiRows],
  );

  // Cloudscape reports widths positionally against the columns it actually
  // RENDERED, so this has to map them back through the same filtered list rather
  // than the full COLUMNS. Hiding the three relevance-less columns shifts every
  // index after the first, and iterating COLUMNS would persist each width under
  // a neighbouring column's id — silently, and then restore it wrong on reload.
  const visibleColumnIds = useMemo(
    () => columnDefinitions.map((c) => c.id).filter((id): id is string => !!id),
    [columnDefinitions],
  );

  const handleColumnWidthsChange = useCallback(
    (widths: readonly number[]) => {
      setColumnWidths((prev) => {
        const next = { ...prev };
        visibleColumnIds.forEach((id, i) => {
          if (typeof widths[i] === 'number') next[id] = widths[i];
        });
        saveColumnWidths(next);
        return next;
      });
    },
    [visibleColumnIds],
  );
  // Result of the most recent ClaimAccounts call — rendered via Flashbar
  // so the user can see "X added, Y skipped" before the panel navigates
  // away. Cleared on each new Confirm attempt.
  const [flash, setFlash] = useState<FlashbarProps.MessageDefinition[]>([]);

  // Reconcile selection/expansion with the latest snapshot. Without this,
  // when Rocky refines the candidates (e.g. removes some accounts), the
  // `selected` array would still hold row references no longer present —
  // Confirm would then claim things the user can't see. Same hazard for
  // `expandedItems`. Rebind to fresh row objects so Cloudscape's reference
  // equality stays happy after a snapshot refresh.
  useEffect(() => {
    const accountById = new Map(accountRows.map((r) => [r.account_id, r]));
    const oppKey = (accountId: string, oppId: string) => `${accountId}|${oppId}`;
    const presentOpps = new Set<string>();
    for (const a of accountRows) {
      for (const o of a.open_opps) presentOpps.add(oppKey(a.account_id, o.opp_id));
    }
    setSelected((prev) =>
      prev.flatMap((r) => {
        if (isAccountRow(r)) {
          const fresh = accountById.get(r.account_id);
          return fresh ? [fresh as TableRow] : [];
        }
        if (!presentOpps.has(oppKey(r.parent_account_id, r.opp_id))) return [];
        // Rebind to the freshly-materialised opp child so identity matches
        // the row Cloudscape renders.
        const parent = accountById.get(r.parent_account_id);
        if (!parent) return [];
        const opp = parent.open_opps.find((o) => o.opp_id === r.opp_id);
        if (!opp) return [];
        return [{
          ...opp,
          kind: 'opp',
          parent_account_id: parent.account_id,
          parent_account_name: parent.account_name,
        } satisfies OppTableRow as TableRow];
      }),
    );
    // Default-expand every account with children. Cloudscape's "select all"
    // header checkbox only ticks currently-visible rows, so collapsed opp
    // children silently drop out of the claim payload.
    setExpandedItems(accountRows.filter((a) => a.open_opps.length > 0));
  }, [accountRows]);

  // Derive claim payload. Two independent buckets — a user can tick an
  // account row AND some of its opp children in the same wave; the account
  // tick becomes an EXPLORE claim on motion_accounts, each ticked opp
  // becomes an ACCELERATE claim on motion_opps. They land in the same
  // Motion under the same wave_id, but in different pool tables.
  //
  //   ticked account   → account entry (EXPLORE)
  //   ticked opp       → opp entry (ACCELERATE); parent-account tick does
  //                       NOT swallow it.
  const claimPayload = useMemo(() => {
    const accountIds: string[] = [];
    const oppsByAccount = new Map<string, string[]>();
    for (const r of selected) {
      if (isAccountRow(r)) {
        accountIds.push(r.account_id);
      } else if (isOppRow(r)) {
        const list = oppsByAccount.get(r.parent_account_id) ?? [];
        list.push(r.opp_id);
        oppsByAccount.set(r.parent_account_id, list);
      }
    }
    return { accountIds, oppsByAccount };
  }, [selected]);

  // Split the pending claim count into accounts vs opps so the Confirm
  // button can show both in "(A accounts · N opps)" form — a whole-account
  // tick becomes 1 account (opps ride along, not counted separately); an
  // opp-only tick becomes 1 opp.
  const claimCounts = useMemo(() => {
    const accounts = claimPayload.accountIds.length;
    let opps = 0;
    for (const ids of claimPayload.oppsByAccount.values()) opps += ids.length;
    return { accounts, opps, total: accounts + opps };
  }, [claimPayload]);

  const confirmButtonLabel = useMemo(() => {
    const { accounts, opps, total } = claimCounts;
    if (total === 0) return 'Confirm & Add to Motion';
    // Both non-zero: "1 account · 3 opps". Only one side non-zero: "3 opps"
    // or "2 accounts". Singular/plural agreement matters for readability
    // — "1 opp" not "1 opps".
    const parts: string[] = [];
    if (accounts > 0) parts.push(`${accounts} ${accounts === 1 ? 'account' : 'accounts'}`);
    if (opps > 0) parts.push(`${opps} ${opps === 1 ? 'opp' : 'opps'}`);
    return `Confirm & Add to Motion (${parts.join(' · ')})`;
  }, [claimCounts]);

  // -- Actions ---------------------------------------------------------------

  const askRocky = useCallback(() => {
    setError(null);
    // Points at ivthub-relevance-query, which turns what the user says into a
    // ListRelevantPipelines request and sends it back as a query_spec; the panel
    // runs it. The older ivthub-motion-wave-select is still reachable from the
    // upload flow below — that one produces a candidate SET, which is a different
    // statement and lands on `results` rather than `query_spec`.
    //
    // The motion id is passed because the skill needs it for the envelope, and
    // the panel's own asset resolution is not visible to Rocky.
    //
    // Naming the current filters matters: the skill REPLACES the spec rather than
    // merging, so an agent that only hears the new condition would silently drop
    // the ones already applied.
    const current = specTokens.length
      ? ` Currently applied: ${specTokens.map((t) => t.label).join('; ')} — keep these unless I say otherwise.`
      : '';
    sendPrompt(
      `Use the ivthub-relevance-query skill for motion ${motionId} to narrow the ` +
      `recommended opportunities.${current} Ask me what to filter by if I have not said yet.`,
    );
  }, [motionId, sendPrompt, specTokens]);

  const uploadOppList = useCallback(() => {
    setError(null);
    // First call carries no material — skill responds with `needs_input`
    // and Rocky asks the user in chat to paste opp ids/names, attach a
    // CSV/TXT file, or describe by criteria (territory / owner). The
    // second invocation (driven by the user's reply) populates --ids /
    // --file / --user-message and produces the state_snapshot.
    sendPrompt(
      `Use the ivthub-motion-wave-select skill to import opps into motion ` +
      `${motionId}. intent=import_opps.`,
    );
  }, [motionId, sendPrompt]);

  const handleConfirm = useCallback(async () => {
    if (claimCounts.total === 0) return;
    setConfirming(true);
    setError(null);
    setFlash([]);

    // Flatten the two-bucket selection into the Smithy `Claim[]` shape.
    // Need a fresh accountById map here so opp lookups can pull oppName /
    // ARR / stage from the live rows rather than capturing stale ones.
    const accountById = new Map(accountRows.map((r) => [r.account_id, r]));
    const claims = buildClaims(claimPayload, accountById);
    if (claims.length === 0) {
      setError('Selection resolved to zero claims; nothing to submit.');
      setConfirming(false);
      return;
    }

    try {
      // waveId is client-generated (opaque, 32-char lowercase hex). Backend
      // UPSERTs a motion_waves row from it (name='claim', method='MANUAL')
      // as part of the same transaction that writes motion_accounts/opps.
      // No separate CreateWave call.
      const waveId = newWaveId();

      const out = await getIvtClient().send(
        new ClaimCandidatesCommand({
          motionId,
          waveId,
          claims,
        }),
      );
      const result: ClaimAccountsResult = {
        claimed: out.claimed ?? 0,
        skipped: out.skipped ?? 0,
        conflicts: (out.conflicts ?? []).map((c) => ({
          accountName: c.accountName ?? '',
          opptyId: c.opptyId,
          reason: c.reason ?? 'unknown',
        })),
      };

      // Banner — success summary; conflicts (if any) listed by accountName.
      const messages: FlashbarProps.MessageDefinition[] = [];
      if (result.claimed > 0) {
        messages.push({
          type: result.skipped > 0 ? 'warning' : 'success',
          content:
            `Added ${result.claimed} ${result.claimed === 1 ? 'claim' : 'claims'} to Motion #${motionId}` +
            (result.skipped > 0 ? `, skipped ${result.skipped}.` : '.'),
          dismissible: false,
        });
      }
      if (result.conflicts.length > 0) {
        messages.push({
          type: 'warning',
          header: `${result.conflicts.length} conflict${result.conflicts.length === 1 ? '' : 's'}`,
          content: result.conflicts
            .map((c) => `${c.accountName}${c.opptyId ? ` · ${c.opptyId}` : ''}: ${c.reason}`)
            .join(' · '),
          dismissible: false,
        });
      }
      setFlash(messages);

      // Hand back to WaveCreationPage so it can navigate to the motion
      // detail. Brief delay (1.5s) so the user sees the success banner
      // before the route swap. Slightly shorter when there are conflicts
      // would be wrong — they NEED to read it; keep the same 1.5s grace.
      // NOTE: `confirming` stays true through the timeout so the button
      // is disabled during the 1.5s grace — otherwise the user could
      // double-click Confirm and create a duplicate Wave + double-claim.
      // Only the error path resets it, letting the user retry.
      window.setTimeout(() => onConfirmed?.(result.claimed, result.skipped), 1500);
    } catch (e) {
      setError(denialHint(e) ?? ((e as Error).message || 'Confirm failed'));
      setConfirming(false);
    }
  }, [motionId, claimPayload, claimCounts.total, accountRows, onConfirmed]);

  // -- Render ----------------------------------------------------------------

  const empty = rows.length === 0;
  // Description is the "where am I" context line under the title. Always
  // anchored on the Motion id; appended with candidate counts once Rocky has
  // returned data. The empty-state guidance lives only in the Table empty
  // slot (kept out of the description to avoid duplication).
  // scoreCounts covers the FULL filtered set and ignores the score floor, so
  // the "N more at 3+" figure tells the user what relaxing the threshold would
  // add — without a second request, and without them having to guess.
  // Total at the CURRENT floor, summed from scoreCounts rather than refetched:
  // scoreCounts deliberately ignores minRelevanceScore and covers the whole
  // filtered set, so every threshold's total is already in hand.
  const perScore = [
    apiCounts?.score1 ?? 0,
    apiCounts?.score2 ?? 0,
    apiCounts?.score3 ?? 0,
    apiCounts?.score4 ?? 0,
    apiCounts?.score5 ?? 0,
  ];
  const totalAtFloor = perScore
    .slice(effectiveMinRelevance - 1)
    .reduce((a, b) => a + b, 0);
  // What dropping one more notch would add. Nothing to offer at floor 1.
  const relaxable = effectiveMinRelevance > 1 ? perScore[effectiveMinRelevance - 2] : 0;
  // OPPORTUNITIES loaded, not accounts. rows.length counts account rows, and an
  // account holds several opportunities — reporting it as "candidates" understates
  // what is on screen, and after a Load more it would barely move while the table
  // grew. Total comes from scoreCounts, which covers the full filtered set, so
  // "X of Y" is available without another request.
  const loadedOpps = useMemo(
    () => rows.reduce((n, a) => n + a.open_opps.length, 0),
    [rows],
  );
  const summaryText =
    `Motion #${motionId}` +
    (empty
      ? ''
      : usingApiRows
        ? ` · ${loadedOpps}${totalAtFloor > loadedOpps ? ` of ${totalAtFloor}` : ''} opportunities` +
          ` across ${rows.length} accounts · relevance ${effectiveMinRelevance}+` +
          (relaxable > 0
            ? ` · ${relaxable} more at ${effectiveMinRelevance - 1}+`
            : '')
        : ` · ${snapshot?.match_count ?? rows.length} candidates` +
          (snapshot?.has_pipeline_count != null
            ? ` · ${snapshot.has_pipeline_count} with pipeline, ${snapshot.no_pipeline_count ?? 0} without`
            : ''));

  return (
    <Container
      header={
        <Header
          variant="h2"
          description={summaryText}
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button
                variant="normal"
                onClick={uploadOppList}
                disabled={chatBusy}
                loading={chatBusy}
              >
                Upload Opportunity List
              </Button>
              <Button
                variant="normal"
                onClick={askRocky}
                disabled={chatBusy}
                loading={chatBusy}
              >
                {/* Not "recommend" any more: the list is already on screen when
                    the panel opens. What Rocky adds is turning a sentence into a
                    filter, which is what this asks for. */}
                Refine with Rocky
              </Button>
              {onCancel && (
                <Button variant="link" onClick={onCancel}>Cancel</Button>
              )}
              <Button
                variant="primary"
                disabled={claimCounts.total === 0 || confirming}
                loading={confirming}
                onClick={handleConfirm}
              >
                {confirmButtonLabel}
              </Button>
            </SpaceBetween>
          }
        >
          Create Wave
        </Header>
      }
    >
      {/* Query controls, deliberately minimal. Finding a specific opportunity is
          what the chat input is for — an on-page search box duplicated it, and the
          two include flags are low-frequency "widen" toggles. Rocky can still set
          all of them, and the token group below keeps whatever it set visible and
          removable, so nothing became unreachable.

          The relevance floor stays because it is the one control with an immediate
          quantified trade-off: the header already says how many more rows a lower
          floor would add, which is the decision this control exists to make. */}
      {usingApiRows && (
        <Box margin={{ bottom: 's' }}>
          <SpaceBetween size="xs">
            <FormField label="Minimum relevance">
              <SegmentedControl
                selectedId={String(effectiveMinRelevance)}
                onChange={({ detail }) =>
                  setSpec((p) => ({ ...p, minRelevanceScore: Number(detail.selectedId) }))
                }
                /* One option per floor `resolveMinRelevance` can return, or a spec
                   asking for the missing one renders the control with nothing
                   selected — the failure that function exists to prevent. 2 is a
                   floor the server accepts, so clamping it into this set instead
                   would answer a narrower question than the user asked. */
                options={[
                  { id: '5', text: '5' },
                  { id: '4', text: '4+' },
                  { id: '3', text: '3+' },
                  { id: '2', text: '2+' },
                  { id: '1', text: 'All' },
                ]}
                label="Minimum relevance"
              />
            </FormField>
            {specTokens.length > 0 && (
              <TokenGroup
                items={specTokens.map((t) => ({ label: t.label, dismissLabel: `remove ${t.label}` }))}
                onDismiss={({ detail }) => {
                  const token = specTokens[detail.itemIndex];
                  if (token) setSpec((p) => removeToken(p, token));
                }}
              />
            )}
          </SpaceBetween>
        </Box>
      )}

      {error && (
        <Box margin={{ bottom: 's' }}>
          <StatusIndicator type="error">{error}</StatusIndicator>
        </Box>
      )}

      {flash.length > 0 && (
        <Box margin={{ bottom: 's' }}>
          <Flashbar items={flash} />
        </Box>
      )}

      <Table<TableRow>
        columnDefinitions={columnDefinitions}
        items={accountRows}
        trackBy={trackBy}
        selectionType="multi"
        selectedItems={selected}
        resizableColumns
        onColumnWidthsChange={({ detail }) => handleColumnWidthsChange(detail.widths)}
        // Both account and opp rows are selectable: ticking an account
        // claims the whole account (all open opps come with it); ticking
        // opps individually claims just those opps. Disambiguation lives
        // in `claimPayload` above, where an account selection supersedes
        // any opp selections under it.
        onSelectionChange={({ detail }) => setSelected([...detail.selectedItems])}
        expandableRows={{
          // Children of an account row are its opps. Accounts with no open
          // opps stay non-expandable (the chevron is hidden by Cloudscape).
          getItemChildren: (r) =>
            isAccountRow(r) ? toOppChildren(r) : [],
          isItemExpandable: (r) =>
            isAccountRow(r) && r.open_opps.length > 0,
          expandedItems,
          onExpandableItemToggle: ({ detail }) => {
            if (!isAccountRow(detail.item)) return;
            const acc = detail.item;
            setExpandedItems((cur) =>
              detail.expanded
                ? [...cur, acc]
                : cur.filter((x) => x.account_id !== acc.account_id),
            );
          },
        }}
        renderAriaLive={({ firstIndex, lastIndex, totalItemsCount }) =>
          `Displaying items ${firstIndex} to ${lastIndex} of ${totalItemsCount}`
        }
        sortingDisabled={false}
        empty={
          <Box textAlign="center" padding="m">
            {chatBusy ? (
              <SpaceBetween direction="horizontal" size="xs" alignItems="center">
                <Spinner />
                <span>Rocky is working on the recommendation…</span>
              </SpaceBetween>
            ) : apiLoading ? (
              <SpaceBetween direction="horizontal" size="xs" alignItems="center">
                <Spinner />
                <span>Loading recommended opportunities…</span>
              </SpaceBetween>
            ) : apiError ? (
              // Say the default list failed, and that Rocky is still a way
              // through — a dead panel with no explanation is the worse outcome.
              <SpaceBetween size="xs">
                <StatusIndicator type="error">
                  Could not load recommendations: {apiError}
                </StatusIndicator>
                <span>Refine with Rocky, or reload the page to retry.</span>
              </SpaceBetween>
            ) : assetsExcluded ? (
              // A distinct message because nothing was queried: the requested
              // assets are not this Motion's, so lowering the threshold — what the
              // generic empty state advises — would change nothing.
              <span>
                The requested assets are not part of this Motion, so there is nothing
                to recommend. Remove the asset filter above, or ask Rocky for assets
                this Motion covers.
              </span>
            ) : (
              <span>
                No opportunities scored {effectiveMinRelevance} or above for this
                Motion&apos;s assets. Lower the relevance threshold, or widen the Motion&apos;s
                asset set.
              </span>
            )}
          </Box>
        }
        ariaLabels={{
          selectionGroupLabel: 'Candidate selection',
          allItemsSelectionLabel: () => 'select all',
          itemSelectionLabel: (_, item) =>
            isAccountRow(item)
              ? `select account ${item.account_name}`
              : `select opportunity ${item.opp_name}`,
        }}
        // NOTE: loading={confirming} was here but Cloudscape Table treats
        // loading=true by replacing the body with a "Loading…" placeholder,
        // which blanked the whole candidate list the instant the user hit
        // Confirm. The Confirm button already renders its own spinner +
        // disabled state — that's the right place to signal submission
        // progress. Keep the table visible so the user still sees what
        // they just claimed while the Flashbar appears.
        //
        // Load more rather than auto-fetching every page: this is a curation
        // screen, and the alternative to a button is either dumping hundreds of
        // rows on the user or truncating silently at the first page — which is
        // what this replaced. The footer only appears while a token exists, so
        // its absence is itself the signal that everything is loaded.
        footer={
          usingApiRows && apiToken ? (
            <Box textAlign="center">
              <SpaceBetween size="xs">
                {/* Beside the button, because the button IS the retry. Putting a
                    paging failure in the Flashbar above would separate it from the
                    control that fixes it, and compete with the claim result. */}
                {moreError && (
                  <StatusIndicator type="error">
                    Could not load the next page: {moreError}
                  </StatusIndicator>
                )}
                <Button onClick={loadMore} loading={loadingMore} disabled={loadingMore}>
                  {loadingMore ? 'Loading…' : moreError ? 'Retry' : 'Load more opportunities'}
                </Button>
              </SpaceBetween>
            </Box>
          ) : undefined
        }
      />
    </Container>
  );
}
